import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";

function Results({ poll }) {
  const total = poll.results.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="space-y-4" data-testid="poll-results">
      {poll.options.map((o) => {
        const r = poll.results.find((x) => x.option_id === o.id);
        const pct = Math.round((r.count / total) * 100);
        return (
          <div key={o.id} data-testid={`poll-result-${o.id}`}>
            <div className="mb-1.5 flex justify-between text-sm"><span className="font-medium">{o.text}</span><span className="text-muted-foreground">{r.count} · {pct}%</span></div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
            {r.voters.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{r.voters.join(", ")}</p>}
          </div>
        );
      })}
    </div>
  );
}

export default function SondaggioDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [p, setP] = useState(null);
  const [sel, setSel] = useState([]);
  const load = useCallback(() => api.get(`/polls/${id}`).then((r) => setP(r.data)).catch(() => nav("/sondaggi")), [id, nav]);
  useEffect(() => { load(); }, [load]);

  const vote = async () => {
    try { await api.post(`/polls/${id}/vote`, { option_ids: sel }); toast.success("Voto registrato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const close = async () => { await api.post(`/polls/${id}/close`); toast.success("Sondaggio chiuso"); load(); };
  const remove = async () => { await api.delete(`/polls/${id}`); toast.success("Sondaggio eliminato"); nav("/sondaggi"); };

  if (!p) return null;
  const canVote = p.is_open && !p.has_voted;
  return (
    <div className="max-w-2xl" data-testid="poll-detail-page">
      <Link to="/sondaggi" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Sondaggi</Link>
      <div className="fade-up rounded-lg border bg-card p-6 sm:p-8">
        <p className="text-xs text-muted-foreground">{p.is_open ? "Aperto" : "Chiuso"} · {p.participants} partecipanti{p.anonymous && " · anonimo"}{p.closes_at && ` · chiude ${fmtDateTime(p.closes_at)}`}</p>
        <h1 className="mt-2 text-2xl font-bold" data-testid="poll-question">{p.question}</h1>
        {p.description && <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>}
        <div className="mt-8">
          {canVote ? (
            <div className="space-y-3">
              {p.multiple ? (
                p.options.map((o) => (
                  <label key={o.id} className="flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm transition-colors hover:bg-muted">
                    <Checkbox checked={sel.includes(o.id)} onCheckedChange={(c) => setSel(c ? [...sel, o.id] : sel.filter((x) => x !== o.id))} data-testid={`poll-option-${o.id}`} />{o.text}
                  </label>
                ))
              ) : (
                <RadioGroup value={sel[0] || ""} onValueChange={(v) => setSel([v])} className="space-y-2">
                  {p.options.map((o) => (
                    <label key={o.id} className="flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm transition-colors hover:bg-muted">
                      <RadioGroupItem value={o.id} data-testid={`poll-option-${o.id}`} />{o.text}
                    </label>
                  ))}
                </RadioGroup>
              )}
              <Button onClick={vote} disabled={!sel.length} className="mt-2" data-testid="poll-vote-button">Invia voto</Button>
              {isAdmin && p.results && <div className="mt-8 border-t pt-6"><Results poll={p} /></div>}
            </div>
          ) : p.results ? <Results poll={p} /> : <p className="text-sm text-muted-foreground">I risultati saranno disponibili alla chiusura.</p>}
        </div>
        {isAdmin && (
          <div className="mt-8 flex gap-2 border-t pt-5">
            {p.is_open && <Button variant="outline" onClick={close} data-testid="poll-close-button"><Lock className="mr-1.5 h-4 w-4" />Chiudi</Button>}
            <Button variant="outline" className="text-destructive" onClick={remove} data-testid="poll-delete-button"><Trash2 className="mr-1.5 h-4 w-4" />Elimina</Button>
          </div>
        )}
      </div>
    </div>
  );
}
